import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo } from "react";
import Header from "../../../../components/header";
import { useFooter } from "../../../../contexts/footer-context";
import {
  HokkaidoMynaApplicationResult,
  ProcessingView,
} from "../../../../features/hokkaido-myna-application/components";
import { getCallbackState } from "../../../../features/hokkaido-myna-application/state";
import { ERROR_CODES } from "../../../../libs/errors";

// MynaConnect からの戻り先。バックエンドがこのパスを固定で組み立てているので変更しない
export const Route = createFileRoute("/_authed/hkp/apply/callback")({
  beforeLoad: ({ context: { tenant } }) => {
    if (tenant.slug !== "hokkaido") {
      throw redirect({ to: "/" });
    }
  },
  component: HokkaidoMynaApplicationCallbackPage,
});

const POLL_INTERVAL_MS = 1000;

function HokkaidoMynaApplicationCallbackPage() {
  const { client } = Route.useRouteContext();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setDisabled: setFooterDisabled } = useFooter();

  const callback = useQuery({
    queryKey: ["hokkaido-r8-householder-myna-application-callback"],
    queryFn: () =>
      client.handleHokkaidoR8HouseholderMynaApplicationCallback({}),
    // 付与まで進む呼び出しなので、確定後に再取得させない
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: (query) =>
      !query.state.error && query.state.data?.outcome.case === "pending"
        ? POLL_INTERVAL_MS
        : false,
    // 失敗はサーバーエラー画面で受ける（Sentry へは meta 経由で送られる）
    throwOnError: false,
    meta: {
      errorCode:
        ERROR_CODES.CONNECT_HANDLE_HOKKAIDO_R8_HOUSEHOLDER_MYNA_APPLICATION_CALLBACK_FAILED,
    },
  });

  const state = useMemo(
    () => getCallbackState(callback.data, callback.error),
    [callback.data, callback.error],
  );

  const isProcessing = state.type === "processing" || state.type === "redirect";

  useEffect(() => {
    if (!isProcessing) return;
    setFooterDisabled(true);
    return () => setFooterDisabled(false);
  }, [isProcessing, setFooterDisabled]);

  useEffect(() => {
    if (state.type === "redirect") {
      window.location.href = state.uri;
    }
    if (state.type === "granted") {
      queryClient.resetQueries({ queryKey: ["transactions"] });
    }
  }, [state, queryClient]);

  const handleGoHome = useCallback(() => {
    navigate({ to: "/", replace: true });
  }, [navigate]);

  const handleRetry = useCallback(() => {
    navigate({ to: "/hkp/apply", replace: true });
  }, [navigate]);

  return (
    <>
      <Header title="" onClickBack={isProcessing ? undefined : handleGoHome} />
      {state.type === "processing" || state.type === "redirect" ? (
        <ProcessingView />
      ) : (
        <HokkaidoMynaApplicationResult
          view={state}
          onGoHome={handleGoHome}
          onRetry={handleRetry}
        />
      )}
    </>
  );
}
